/**
 * A seated bill's edits to its booking, held until the bill's save succeeds (RESERVATIONS_V2_PLAN.md §8.6 RV6; decision 169;
 * bridge B5). The dine-in and customer forms submit BEFORE the bill is saved, and a save that hangs, fails or is abandoned
 * must leave the booking as it was, or the booking and its bill say different things (device run 6, finding F1: the booking
 * said Table 3 with four guests while the bill stayed on RistaB with two). So a submit only `holdBillEdit`s, and the host's
 * `sale/save` saga calls `releaseHeldBillEdit` with the server's answer once the save has succeeded. Nothing here awaits:
 * the bill's own save never waits for the booking (decision 145).
 *
 * The backend also moves a booking that holds exactly one table onto the bill's table on every successful save of a bill
 * with a `reservationId`, so a held edit is checked against the bill that was actually saved before it is sent: a part the
 * saved bill no longer agrees with (a Done followed by a Discard, then an unchanged save) is dropped, not sent.
 *
 * State is one module-level map, because the form that holds an edit and the saga that releases it share nothing else.
 */
import { isPlainRecord } from "../api/wireGuards";
import type { SaleEditContact, SaleEditOfBooking } from "./fromSale";
import { billServiceLabelOf, bookingOfBill, type BillBooking } from "./fromSaleForm";
import type { SyncBillEdit } from "./hooks/useSaleEditSync";

/** One booking's held edit, with the booking it addresses and the function that sends it. */
interface HeldBillEdit {
  /** The booking the bill belongs to, as read from the bill when the edit was held. */
  booking: BillBooking;
  /** What the bill's forms have said about the booking so far, merged across submits. */
  edit: SaleEditOfBooking;
  /** The function that sends the edit onto the booking once the bill has saved. */
  send: SyncBillEdit;
}

/** The held edits, by the booking's id: at most one per booking, since one bill belongs to one booking. */
const heldEdits = new Map<string, HeldBillEdit>();

/** The contact two submits said, field by field: a field the newer submit typed replaces the older one; the rest stay. */
function mergedContact(older: SaleEditContact | undefined, newer: SaleEditContact | undefined): SaleEditContact | undefined {
  if (older === undefined || newer === undefined) {
    return newer ?? older;
  }
  return { ...older, ...newer };
}

/** The edit two submits said, part by part: a part the newer submit gives replaces the older one; the rest stay. */
function mergedEdit(older: SaleEditOfBooking, newer: SaleEditOfBooking): SaleEditOfBooking {
  const merged: SaleEditOfBooking = {};
  const tableNow = newer.tableNow ?? older.tableNow;
  const guestCountNow = newer.guestCountNow ?? older.guestCountNow;
  const contactNow = mergedContact(older.contactNow, newer.contactNow);
  if (tableNow !== undefined) {
    merged.tableNow = tableNow;
  }
  if (guestCountNow !== undefined) {
    merged.guestCountNow = guestCountNow;
  }
  if (contactNow !== undefined) {
    merged.contactNow = contactNow;
  }
  return merged;
}

/**
 * Holds a bill's edit for its booking until `releaseHeldBillEdit` is told the bill saved. When an edit is already held for
 * the booking (the cashier pressed Done twice, or the customer form and the dine-in form both submitted), the two are
 * merged with the newer one's parts winning, and the newer `booking` and `send` are kept.
 */
export function holdBillEdit(booking: BillBooking, edit: SaleEditOfBooking, send: SyncBillEdit): void {
  const held = heldEdits.get(booking.reservationId);
  heldEdits.set(booking.reservationId, { booking, edit: held === undefined ? edit : mergedEdit(held.edit, edit), send });
}

/** The edit's parts that still agree with the bill the server saved: the table and guest count are dropped when the saved bill says otherwise. */
function editAgreeingWith(edit: SaleEditOfBooking, savedBill: unknown): SaleEditOfBooking {
  const savedLabel = billServiceLabelOf(isPlainRecord(savedBill) ? savedBill.serviceLabel : undefined);
  const agreeing: SaleEditOfBooking = {};
  const tableDisagrees = edit.tableNow !== undefined && savedLabel.resourceId !== undefined && savedLabel.resourceId !== edit.tableNow.id;
  const guestsDisagree = edit.guestCountNow !== undefined && savedLabel.count !== undefined && savedLabel.count !== edit.guestCountNow;
  if (edit.tableNow !== undefined && !tableDisagrees) {
    agreeing.tableNow = edit.tableNow;
  }
  if (edit.guestCountNow !== undefined && !guestsDisagree) {
    agreeing.guestCountNow = edit.guestCountNow;
  }
  if (edit.contactNow !== undefined) {
    agreeing.contactNow = edit.contactNow;
  }
  return agreeing;
}

/**
 * Sends the edit held for the saved bill's booking, once, after a successful `sale/save`; `savedBill` is the server's answer.
 * Nothing is sent for a bill that is no booking's, for a booking with nothing held, or when no part of the held edit agrees
 * with the saved bill. It never throws and never awaits, so whatever goes wrong here cannot affect the bill's save.
 */
export function releaseHeldBillEdit(savedBill: unknown): void {
  try {
    const booking = isPlainRecord(savedBill) ? bookingOfBill(savedBill) : undefined;
    const held = booking === undefined ? undefined : heldEdits.get(booking.reservationId);
    if (booking === undefined || held === undefined) {
      return;
    }
    heldEdits.delete(booking.reservationId);
    const edit = editAgreeingWith(held.edit, savedBill);
    if (edit.tableNow !== undefined || edit.guestCountNow !== undefined || edit.contactNow !== undefined) {
      held.send(held.booking, edit);
    }
  } catch {
    // A failure here must never reach the bill's save; the booking simply keeps what it had.
  }
}

/** Forgets every held edit. For tests only: the map is module-wide, so a test starts from an empty one. */
export function clearHeldBillEdits(): void {
  heldEdits.clear();
}
